import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Mail, Loader2, AlertCircle, ExternalLink, RefreshCw, Save } from 'lucide-react';
import { useToast } from '@/components/use-toast';
import { User } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { useTranslation } from '@/components/utils/translations';

const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'https://api.neogtm.com';
  }
  return 'https://api.neogtm.com';
};

const API_BASE_URL = getApiBaseUrl();

function sanitizeSignatureEditorHtml(html) {
  return String(html || '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<meta[^>]*>/gi, '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/\sdir\s*=\s*["']rtl["']/gi, ' dir="ltr"')
    .replace(/\sdir\s*=\s*["']auto["']/gi, ' dir="ltr"');
}

function applySignatureHtmlToEditor(editorEl, html) {
  if (!editorEl) return;
  const clean = sanitizeSignatureEditorHtml(html);
  if (editorEl.innerHTML !== clean) {
    editorEl.innerHTML = clean;
  }
}

export default function SettingsGmail({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  const user = contextUser;
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isLoadingAliases, setIsLoadingAliases] = useState(false);
  const [isLoadingSignature, setIsLoadingSignature] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [selectedAlias, setSelectedAlias] = useState('');
  const [signature, setSignature] = useState(null);
  const [signatureHtml, setSignatureHtml] = useState('');
  const [savedSignature, setSavedSignature] = useState('');
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [error, setError] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true);
  const { toast } = useToast();
  const signatureRef = useRef(null);
  const signatureEditorFocusedRef = useRef(false);
  const oauthListenerRef = useRef(null);
  const oauthTimeoutRef = useRef(null);
  const oauthPollRef = useRef(null);

  useEffect(() => {
    loadUserAndStatus();
  }, [contextUser]);

  useEffect(() => {
    return () => {
      if (oauthListenerRef.current) {
        window.removeEventListener('message', oauthListenerRef.current);
        oauthListenerRef.current = null;
      }
      if (oauthTimeoutRef.current) clearTimeout(oauthTimeoutRef.current);
      if (oauthPollRef.current) clearInterval(oauthPollRef.current);
    };
  }, []);

  useEffect(() => {
    if (gmailConnected && apiAvailable) {
      loadAliases();
    }
  }, [gmailConnected, apiAvailable]);

  useEffect(() => {
    if (selectedAlias && gmailConnected && apiAvailable) {
      loadSignature(selectedAlias);
    }
  }, [selectedAlias, gmailConnected, apiAvailable]);

  useEffect(() => {
    if (contextUser && apiAvailable) {
      loadSavedSignature();
    }
  }, [contextUser, apiAvailable]);

  // Sync HTML into the editor only when not typing (avoid dangerouslySetInnerHTML on input).
  useEffect(() => {
    if (signatureEditorFocusedRef.current) return;
    applySignatureHtmlToEditor(signatureRef.current, signatureHtml);
  }, [signatureHtml]);

  const setEditorSignature = (html) => {
    const clean = sanitizeSignatureEditorHtml(html);
    setSignatureHtml(clean);
    applySignatureHtmlToEditor(signatureRef.current, clean);
  };

  const loadUserAndStatus = async () => {
    if (!contextUser) return;
    try {
      setIsLoading(true);
      setError(null);

      const userId = contextUser.user_id || contextUser.id || contextUser.email;

      if (!userId) {
        throw new Error('Unable to identify user');
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/gmail/status?tenant_id=${tenantId}&user_id=${userId}`,
          { credentials: 'include' }
        );

        if (response.ok) {
          const data = await response.json();
          setGmailConnected(data.connected);
          setGmailEmail(data.email);
          setApiAvailable(true);

          // Load preferred alias from user profile
          if (contextUser.preferred_gmail_alias) {
            setSelectedAlias(contextUser.preferred_gmail_alias);
          }
        } else if (response.status === 404) {
          // API not available yet
          setApiAvailable(false);
          setGmailConnected(false);
          setGmailEmail(null);
        } else {
          setGmailConnected(false);
          setGmailEmail(null);
        }
      } catch (apiError) {
        console.log('Gmail API not available:', apiError.message);
        setApiAvailable(false);
        setGmailConnected(false);
        setGmailEmail(null);
      }
    } catch (err) {
      console.error('Error loading user:', err);
      setError('Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAliases = async () => {
    if (!apiAvailable || !user || !user.id) {
      console.log('API not available, using fallback storage:', !user ? 'User not loaded' : 'User ID not found');
      return;
    }
    
    try {
      setIsLoadingAliases(true);
      
      const userId = user.user_id || user.id || user.email;
      const response = await fetch(
        `${API_BASE_URL}/api/gmail/aliases?tenant_id=${tenantId}&user_id=${userId}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Failed to load email aliases');

      const data = await response.json();
      if (data.success && data.aliases) {
        setAliases(data.aliases);
        
        // Auto-select default email if no preference set
        if (!selectedAlias && data.default) {
          setSelectedAlias(data.default);
          await savePreferredAlias(data.default);
        }
      }
    } catch (err) {
      console.error('Error loading aliases:', err);
      toast({
        title: t('error'),
        description: t('failedToLoadEmailAddresses'),
        variant: 'destructive'
      });
    } finally {
      setIsLoadingAliases(false);
    }
  };

  const loadSignature = async (email) => {
    if (!apiAvailable || !user || !user.id) return;
    
    try {
      setIsLoadingSignature(true);
      setSignature(null);
      const userId = user.user_id || user.id || user.email;
      const response = await fetch(
        `${API_BASE_URL}/api/gmail/signature?email=${encodeURIComponent(email)}&tenant_id=${tenantId}&user_id=${userId}`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Failed to load signature');

      const data = await response.json();
      if (data.success && data.signature) {
        setSignature(data.signature);
      }
    } catch (err) {
      console.error('Error loading signature:', err);
      setSignature({ html: null, plain: 'No signature configured', email });
    } finally {
      setIsLoadingSignature(false);
    }
  };

  const loadSavedSignature = async () => {
    if (!contextUser) return;
    try {
      const uid = contextUser.user_id || contextUser.id || contextUser.email;
      const res = await fetch(
        `${API_BASE_URL}/api/settings/email-signature?tenant_id=${tenantId}&user_id=${uid}`,
        {
          credentials: 'include',
          headers: { 'x-user-id': String(uid), 'x-tenant-id': String(tenantId) },
        }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.email_signature_html) {
          const clean = sanitizeSignatureEditorHtml(data.email_signature_html);
          setSavedSignature(clean);
          setSignatureHtml(clean);
        }
      }
    } catch (err) {
      console.error('Error loading saved signature:', err);
    }
  };

  const savePreferredAlias = async (email) => {
    try {
      await User.updateMyUserData({
        preferred_gmail_alias: email,
      });
      return true;
    } catch (err) {
      console.error('Error saving preferred alias:', err);
      return false;
    }
  };

  const handleAliasChange = async (email) => {
    setSelectedAlias(email);
    const persisted = await savePreferredAlias(email);
    if (persisted) {
      toast({
        title: t('emailAddressUpdated'),
        description: `${t('emailsWillBeSentFrom')} ${email}`,
      });
    } else {
      toast({
        title: t('emailAddressUpdatedLocalOnly'),
        description: `${t('usingInSessionBackendRejectedAlias')} ${email}`,
        variant: 'destructive',
      });
    }
  };

  const handleRefreshAliases = async () => {
    setAliases([]);
    setSignature(null);
    await loadAliases();
    toast({
      title: t('emailAddressesRefreshed'),
      description: t('emailAliasesReloaded'),
    });
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html) {
      setEditorSignature(html);
    } else if (text) {
      setEditorSignature(text.replace(/\n/g, '<br>'));
    }
  };

  const handleSignatureInput = () => {
    if (signatureRef.current) setSignatureHtml(signatureRef.current.innerHTML);
  };

  const handleSaveSignature = async () => {
    if (!user) return;
    try {
      setIsSavingSignature(true);
      const uid = user.user_id || user.id || user.email;
      const res = await fetch(`${API_BASE_URL}/api/settings/email-signature`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': String(uid),
          'x-tenant-id': String(tenantId),
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: uid,
          email_signature_html: signatureHtml,
        }),
      });
      if (res.ok) {
        setSavedSignature(signatureHtml);
        toast({ title: t('signatureSaved'), description: t('emailSignatureSavedSuccessfully') });
      } else {
        throw new Error('Failed to save signature');
      }
    } catch (err) {
      console.error('Error saving signature:', err);
      toast({ title: t('error'), description: t('failedToSaveSignature'), variant: 'destructive' });
    } finally {
      setIsSavingSignature(false);
    }
  };

  const handleClearSignature = () => {
    setEditorSignature('');
  };

  const handlePullFromGmail = () => {
    const fromGmail = signature?.html || '';
    if (!fromGmail) {
      toast({ title: t('noGmailSignatureFound'), description: t('selectAliasFirstOrSetInGmail') });
      return;
    }
    setEditorSignature(fromGmail);
  };

  const hasUnsavedChanges = signatureHtml !== savedSignature;

  const clearGmailOAuthState = () => {
    localStorage.removeItem('gmail_oauth_tenant_id');
    localStorage.removeItem('gmail_oauth_user_id');
  };

  const handleConnectGmail = async () => {
    if (!apiAvailable) {
      setError('Gmail integration is not available yet. Please contact support.');
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);

      const userId = user.user_id || user.id || user.email;

      if (!userId) throw new Error('User not loaded');

      localStorage.setItem('gmail_oauth_tenant_id', tenantId);
      localStorage.setItem('gmail_oauth_user_id', userId);

      const redirectUri = `${window.location.origin}/SettingsGmailCallback`;
      const response = await fetch(
        `${API_BASE_URL}/api/gmail/oauth/start?tenant_id=${tenantId}&user_id=${encodeURIComponent(userId)}&redirect_uri=${encodeURIComponent(redirectUri)}`,
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to start Gmail authorization');
      }

      const data = await response.json();

      if (!data.authUrl) {
        throw new Error('No authorization URL received from server');
      }

      const width = 520;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        data.authUrl,
        'gmail_oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      let popupUnavailable = !popup;
      if (popup) {
        try {
          popupUnavailable = popup.closed;
        } catch {
          // Cross-origin popup (Google OAuth): COOP blocks window.closed — treat as open
          popupUnavailable = false;
        }
      }
      if (popupUnavailable) {
        window.location.href = data.authUrl;
        return;
      }

      let oauthFinished = false;

      const cleanup = () => {
        if (oauthListenerRef.current) {
          window.removeEventListener('message', oauthListenerRef.current);
          oauthListenerRef.current = null;
        }
        if (oauthTimeoutRef.current) {
          clearTimeout(oauthTimeoutRef.current);
          oauthTimeoutRef.current = null;
        }
        if (oauthPollRef.current) {
          clearInterval(oauthPollRef.current);
          oauthPollRef.current = null;
        }
      };

      const onMessage = (event) => {
        if (event.origin !== window.location.origin) return;

        const payload = event?.data;
        if (!payload || payload.type !== 'oauth:complete' || payload.provider !== 'gmail') return;

        if (oauthFinished) return;
        oauthFinished = true;
        cleanup();
        setIsConnecting(false);

        if (payload.success) {
          setGmailConnected(true);
          setGmailEmail(payload.email);
          toast({
            title: t('gmailConnected'),
            description: payload.email ? `${t('connectedAs')} ${payload.email}` : t('gmailConnectedSuccessfully'),
          });
          loadUserAndStatus();
        } else {
          clearGmailOAuthState();
          setError(payload.error || 'Authorization failed');
        }
      };

      oauthListenerRef.current = onMessage;
      window.addEventListener('message', onMessage);

      // Do not poll popup.closed — Google OAuth is cross-origin and COOP blocks that check.
      // Completion is signaled via postMessage from SettingsGmailCallback (same origin).

      oauthTimeoutRef.current = setTimeout(() => {
        if (oauthFinished) return;
        oauthFinished = true;
        cleanup();
        clearGmailOAuthState();
        setIsConnecting(false);
        setError('Gmail sign-in timed out');
      }, 60000);

    } catch (err) {
      console.error('Error connecting Gmail:', err);
      clearGmailOAuthState();
      setIsConnecting(false);
      setError(err.message || 'Failed to connect Gmail. Please try again.');
    }
  };

  const handleDisconnectGmail = async () => {
    if (!apiAvailable) return;

    try {
      setIsDisconnecting(true);
      setError(null);

      const userId = user.user_id || user.id || user.email;

      if (!userId) throw new Error('User not loaded');

      const response = await fetch(
        `${API_BASE_URL}/api/gmail/disconnect?tenant_id=${tenantId}&user_id=${userId}`,
        { 
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to disconnect Gmail');
      }

      // Verify backend state before showing success. This avoids a false-positive UI
      // when disconnect endpoint returns 200 but status remains connected.
      const statusResponse = await fetch(
        `${API_BASE_URL}/api/gmail/status?tenant_id=${tenantId}&user_id=${userId}`,
        { credentials: 'include' }
      );
      const statusData = statusResponse.ok ? await statusResponse.json() : null;
      const stillConnected = statusData?.connected === true;

      if (!stillConnected) {
        localStorage.removeItem('gmail_connected');
        localStorage.removeItem('gmail_email');

        setGmailConnected(false);
        setGmailEmail(null);
        setAliases([]);
        setSelectedAlias('');
        setSignature(null);

        toast({
          title: t('gmailDisconnected'),
          description: t('gmailAccountDisconnected'),
        });
      } else {
        setGmailConnected(true);
        setGmailEmail(statusData?.email || gmailEmail);
        toast({
          title: t('disconnectNotApplied'),
          description: t('backendStillReportsGmailConnected'),
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Error disconnecting Gmail:', err);
      setError(err.message || 'Failed to disconnect Gmail. Please try again.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="border shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            {t('gmailIntegration')}
          </CardTitle>
          <CardDescription>{t('connectGmailDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">{t('loadingGmailStatus')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5" />
          {t('gmailIntegration')}
        </CardTitle>
        <CardDescription>{t('connectGmailDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!apiAvailable && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('gmailComingSoon')}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-3">
            {gmailConnected ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-medium text-sm">{t('gmailConnected')}</p>
                  {gmailEmail && (
                    <p className="text-xs text-gray-500">{gmailEmail}</p>
                  )}
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-sm">{t('gmailNotConnected')}</p>
                  <p className="text-xs text-gray-500">{t('connectAccountToSendEmails')}</p>
                </div>
              </>
            )}
          </div>

          {gmailConnected ? (
            <Button
              onClick={handleDisconnectGmail}
              disabled={isDisconnecting || !apiAvailable}
              variant="outline"
              size="sm"
            >
              {isDisconnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('disconnecting')}
                </>
              ) : (
                t('disconnect')
              )}
            </Button>
          ) : (
            <Button
              onClick={handleConnectGmail}
              disabled={isConnecting || !apiAvailable}
              size="sm"
              className="bg-[#00C2C2] hover:bg-[#00A3A3]"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('connecting')}
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t('connectGmail')}
                </>
              )}
            </Button>
          )}
        </div>

        {gmailConnected && aliases.length > 0 && apiAvailable && (
          <div className="space-y-4 p-4 border rounded-lg bg-white">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">{t('sendEmailsFrom')}</Label>
              <Button
                onClick={handleRefreshAliases}
                disabled={isLoadingAliases}
                variant="ghost"
                size="sm"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingAliases ? 'animate-spin' : ''}`} />
                {t('refresh')}
              </Button>
            </div>
            
            <Select value={selectedAlias} onValueChange={handleAliasChange} disabled={isLoadingAliases}>
              <SelectTrigger>
                <SelectValue placeholder={t('selectEmailAddress')} />
              </SelectTrigger>
              <SelectContent>
                {aliases.map((alias) => (
                  <SelectItem key={alias.email} value={alias.email}>
                    {alias.displayName ? `${alias.displayName} <${alias.email}>` : alias.email}
                    {alias.isDefault && ` (${t('default')})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="font-semibold">{t('emailSignature')}</Label>
                  <p className="text-xs text-gray-500 mt-1">{t('editSignatureBelowOrPullFromGmail')}</p>
                </div>
                {hasUnsavedChanges && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    {t('unsavedChanges')}
                  </span>
                )}
              </div>

              <div className="space-y-3">
                <div
                  ref={signatureRef}
                  contentEditable
                  suppressContentEditableWarning
                  dir="ltr"
                  lang="en"
                  onFocus={() => {
                    signatureEditorFocusedRef.current = true;
                  }}
                  onBlur={() => {
                    signatureEditorFocusedRef.current = false;
                    handleSignatureInput();
                  }}
                  onPaste={handlePaste}
                  onInput={handleSignatureInput}
                  className="min-h-[120px] max-h-[300px] overflow-y-auto p-3 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#00C2C2] focus:border-transparent text-sm text-left"
                  style={{ lineHeight: '1.5', direction: 'ltr', unicodeBidi: 'plaintext' }}
                  data-placeholder={t('pasteOrTypeSignatureHere')}
                />
                <style>{`
                  [contenteditable]:empty:before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    pointer-events: none;
                  }
                `}</style>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={handleSaveSignature}
                    disabled={isSavingSignature || !signatureHtml || !hasUnsavedChanges}
                    size="sm"
                    className="bg-[#00C2C2] hover:bg-[#00A3A3]"
                  >
                    {isSavingSignature ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('saving')}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {t('saveSignature')}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handlePullFromGmail}
                    disabled={isLoadingSignature || !signature?.html}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingSignature ? 'animate-spin' : ''}`} />
                    {t('useGmailSignature')}
                  </Button>

                  {signatureHtml && (
                    <Button onClick={handleClearSignature} variant="outline" size="sm">
                      {t('clear')}
                    </Button>
                  )}
                </div>

                {savedSignature && !hasUnsavedChanges && (
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle2 className="w-3 h-3" />
                    {t('signatureSavedWillBeUsed')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!gmailConnected && apiAvailable && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-2">{t('whyConnectGmail')}</p>
              <ul className="text-sm space-y-1 ml-4 list-disc">
                <li>{t('gmailBenefitSendPersonalized')}</li>
                <li>{t('gmailBenefitTrackEngagement')}</li>
                <li>{t('gmailBenefitManageOutreach')}</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}